"use strict";

import Crypto from "crypto";
import {hesapTckniOku} from "./OturumKimligi";

const DB_NAME = "EICRAPRO";
const STORE_NAME = "EICRAPRO_CACHE_STORE";
const DB_VERSION = 1;
const DEFAULT_TTL_MINUTES = 60;
const FALLBACK_TAG = "EICRAPRO_CACHE_FALLBACK";

type CacheRecord<T> = {
    key: string;
    data: T;
    expiry: number;
};

const minutesToMs = (minutes: number) => minutes * 60000;
const supportsIndexedDb = typeof indexedDB !== "undefined";

function normalizeTckn(v: string | null | undefined): string | null {
    if (v == null || typeof v !== "string") return null;
    const digits = v.replace(/\D/g, "");
    return /^\d{11}$/.test(digits) ? digits : null;
}

function getUyapCacheScope(): string {
    try {
        // Hesap TCKN kaynağı: imzacıda aktif kimlik, yoksa eskisi gibi sessionStorage.
        const tckn = normalizeTckn(hesapTckniOku(() => sessionStorage.getItem("tckn")));
        if (tckn) return tckn;
    } catch {
        // ignore
    }
    return "__anonymous__";
}

function toStorageKey(logicalKey: string): string {
    return `${getUyapCacheScope()}::${logicalKey}`;
}

function openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        if (!supportsIndexedDb) {
            return reject(new Error("IndexedDB not supported"));
        }
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: "key" });
            }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

function hashKey(value: any): string {
    return Crypto.createHash("sha1")
        .update(JSON.stringify(value))
        .digest()
        .toString("hex");
}

function getFallbackKey(key: string) {
    return `${FALLBACK_TAG}::${key}`;
}

function getFromFallback<T>(key: string): T | undefined {
    try {
        const raw = localStorage.getItem(getFallbackKey(key));
        if (!raw) return undefined;

        const parsed: CacheRecord<T> = JSON.parse(raw);
        if (parsed.expiry < Date.now()) {
            removeFromFallback(key);
            return undefined;
        }
        return parsed.data;
    } catch {
        return undefined;
    }
}

function setToFallback<T>(key: string, data: T, ttlMinutes: number = DEFAULT_TTL_MINUTES) {
    try {
        const record: CacheRecord<T> = {
            key,
            data,
            expiry: Date.now() + minutesToMs(ttlMinutes)
        };
        localStorage.setItem(getFallbackKey(key), JSON.stringify(record));
    } catch {
        // swallow fallback errors
    }
}

function removeFromFallback(key: string) {
    try {
        localStorage.removeItem(getFallbackKey(key));
    } catch {
        // swallow fallback errors
    }
}

async function getFromIndexedDb<T>(key: string): Promise<T | undefined> {
    const db = await openDb();
    return new Promise<T | undefined>((resolve) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const request = store.get(key);

        request.onsuccess = () => {
            const record = request.result as CacheRecord<T> | undefined;
            if (!record) return resolve(undefined);

            if (record.expiry < Date.now()) {
                tx.oncomplete = () => removeFromIndexedDb(key).catch(() => undefined);
                return resolve(undefined);
            }

            resolve(record.data);
        };

        request.onerror = () => resolve(undefined);
    });
}

async function setToIndexedDb<T>(key: string, data: T, ttlMinutes: number = DEFAULT_TTL_MINUTES) {
    const db = await openDb();
    return new Promise<void>((resolve) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        const record: CacheRecord<T> = {
            key,
            data,
            expiry: Date.now() + minutesToMs(ttlMinutes)
        };
        store.put(record);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
    });
}

async function removeFromIndexedDb(key: string) {
    const db = await openDb();
    return new Promise<void>((resolve) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        store.delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
    });
}

export async function getCachedItem<T>(key: string): Promise<T | undefined> {
    const storageKey = toStorageKey(key);
    if (!supportsIndexedDb) {
        return getFromFallback<T>(storageKey);
    }
    try {
        const value = await getFromIndexedDb<T>(storageKey);
        if (value !== undefined) return value;
    } catch {
        // fall through to fallback
    }
    return getFromFallback<T>(storageKey);
}

export async function setCachedItem<T>(key: string, data: T, ttlMinutes: number = DEFAULT_TTL_MINUTES) {
    const storageKey = toStorageKey(key);
    if (!supportsIndexedDb) {
        setToFallback(storageKey, data, ttlMinutes);
        return;
    }
    try {
        await setToIndexedDb(storageKey, data, ttlMinutes);
    } catch {
        setToFallback(storageKey, data, ttlMinutes);
    }
}

export async function removeCachedItem(key: string) {
    const storageKey = toStorageKey(key);
    if (!supportsIndexedDb) {
        removeFromFallback(storageKey);
        return;
    }
    try {
        await removeFromIndexedDb(storageKey);
    } catch {
        removeFromFallback(storageKey);
    }
}

export function buildCacheKeyFromUrl(url: string): string {
    return hashKey(url);
}
